import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import { GoalHeader } from 'shared/components/ui/task-card-task-section/goal-header';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';
import { goalInboxID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';

import { useGoalTree } from '../../hooks/use-goal-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './goal-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const GoalGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useGoalTree({
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }
        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const goalItem = updatedItems.find(({ children }) =>
          children?.includes(item.id),
        );

        taskToUpdate.goalId =
          !goalItem?.id || goalItem.id === goalInboxID ? null : goalItem.id;
        taskToUpdate.lifeAreaId = null;

        updatedDocs.push(taskToUpdate);
      }
      if (item.type === TreeItemTypes.Goal) {
        updatedSections.push({ id: item.id, taskSorting: item.children ?? [] });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // Goal
    if (type === TreeItemTypes.Goal) {
      return (
        <Styled.HeaderContainer>
          <GoalHeader
            goalId={item.sourceId !== goalInboxID ? item.sourceId : undefined}
            goals={goals}
            lifeAreas={lifeAreas}
            onPremium={() => {}}
            isLink
          />
        </Styled.HeaderContainer>
      );
    }

    return null;
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
